if (!customElements.get('brands-list')) {
  class BrandsList extends HTMLElement {
    constructor() {
      super();
      this.brandsData = null;
      this.currentLetter = 'A';
      this.currentTab = 'az';
      this.init();
    }

    init() {
      // Parse JSON data
      const dataScript = this.querySelector('[data-brands-data]');
      if (dataScript) {
        try {
          this.brandsData = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Failed to parse brands data:', e);
          return;
        }
      }

      if (!this.brandsData || !this.brandsData.vendors) {
        return;
      }

      // Set CSS variable for columns
      this.style.setProperty('--brands-list-columns', this.brandsData.columns || 5);

      // Sort vendors alphabetically
      this.brandsData.vendors.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      // Group vendors by first letter
      this.vendorsByLetter = this.groupVendorsByLetter(this.brandsData.vendors);

      // Bind events
      this.bindEvents();

      // Render initial view
      this.renderLetterContent('A');
      this.updateLetterNavState();
    }

    groupVendorsByLetter(vendors) {
      const groups = {};
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

      // Initialize all letter groups
      letters.forEach(letter => {
        groups[letter] = [];
      });
      groups['0-9'] = [];

      vendors.forEach(vendor => {
        const firstChar = vendor.name.charAt(0).toUpperCase();

        if (/[0-9]/.test(firstChar)) {
          groups['0-9'].push(vendor);
        } else if (/[A-Z]/.test(firstChar)) {
          groups[firstChar].push(vendor);
        } else {
          // Handle special characters - put in closest letter or special group
          groups['0-9'].push(vendor);
        }
      });

      return groups;
    }

    bindEvents() {
      // Tab switching
      const tabs = this.querySelectorAll('.brands-list__tab');
      tabs.forEach(tab => {
        tab.addEventListener('click', (e) => this.handleTabClick(e));
      });

      // Letter navigation
      const letterNavItems = this.querySelectorAll('.brands-list__letter-nav-item');
      letterNavItems.forEach(item => {
        item.addEventListener('click', (e) => this.handleLetterClick(e));
      });

      // Search input
      const searchInput = this.querySelector('.brands-list__search-input');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => this.handleSearch(e));
      }
    }

    handleTabClick(e) {
      const tab = e.currentTarget;
      const tabName = tab.dataset.tab;

      if (tabName === this.currentTab) return;

      // Update tab active states
      this.querySelectorAll('.brands-list__tab').forEach(t => {
        t.classList.remove('brands-list__tab--active');
      });
      tab.classList.add('brands-list__tab--active');

      // Show/hide panels
      this.querySelectorAll('.brands-list__panel').forEach(panel => {
        panel.style.display = 'none';
        panel.classList.remove('brands-list__panel--active');
      });

      const activePanel = this.querySelector(`[data-panel="${tabName}"]`);
      if (activePanel) {
        activePanel.style.display = 'block';
        activePanel.classList.add('brands-list__panel--active');
      }

      this.currentTab = tabName;

      // Render content for the active tab
      if (tabName === 'all') {
        this.renderAllBrands();
      }
    }

    handleLetterClick(e) {
      const item = e.currentTarget;
      const letter = item.dataset.letter;

      // Check if this letter has any vendors
      if (!this.vendorsByLetter[letter] || this.vendorsByLetter[letter].length === 0) {
        return;
      }

      // Update active state
      this.querySelectorAll('.brands-list__letter-nav-item').forEach(navItem => {
        navItem.classList.remove('brands-list__letter-nav-item--active');
      });
      item.classList.add('brands-list__letter-nav-item--active');

      this.currentLetter = letter;
      this.renderLetterContent(letter);
    }

    handleSearch(e) {
      const query = e.target.value.toLowerCase().trim();
      this.renderAllBrands(query);
    }

    updateLetterNavState() {
      const letterNavItems = this.querySelectorAll('.brands-list__letter-nav-item');
      letterNavItems.forEach(item => {
        const letter = item.dataset.letter;
        const hasVendors = this.vendorsByLetter[letter] && this.vendorsByLetter[letter].length > 0;

        if (!hasVendors) {
          item.classList.add('brands-list__letter-nav-item--disabled');
        } else {
          item.classList.remove('brands-list__letter-nav-item--disabled');
        }
      });
    }

    renderLetterContent(letter) {
      const container = this.querySelector('.brands-list__letter-content');
      if (!container) return;

      const vendors = this.vendorsByLetter[letter] || [];

      if (vendors.length === 0) {
        container.innerHTML = `
          <div class="brands-list__no-results">
            No brands found starting with "${letter}"
          </div>
        `;
        return;
      }

      const html = `
        <div class="brands-list__brands-grid">
          ${vendors.map(vendor => this.renderBrandLink(vendor)).join('')}
        </div>
      `;

      container.innerHTML = html;
    }

    renderAllBrands(searchQuery = '') {
      const container = this.querySelector('.brands-list__all-brands-content');
      if (!container) return;

      let filteredVendors = this.brandsData.vendors;

      // Apply search filter
      if (searchQuery) {
        filteredVendors = filteredVendors.filter(vendor =>
          vendor.name.toLowerCase().includes(searchQuery)
        );
      }

      if (filteredVendors.length === 0) {
        container.innerHTML = `
          <div class="brands-list__no-results">
            No brands found${searchQuery ? ` matching "${searchQuery}"` : ''}
          </div>
        `;
        return;
      }

      // Group filtered vendors by letter for display
      const grouped = this.groupVendorsByLetter(filteredVendors);
      const letters = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '0-9'];

      let html = '';

      letters.forEach(letter => {
        const vendors = grouped[letter];
        if (vendors && vendors.length > 0) {
          html += `
            <div class="brands-list__letter-section">
              <h3 class="brands-list__letter-header">${letter}</h3>
              <div class="brands-list__all-brands-grid">
                ${vendors.map(vendor => this.renderBrandLink(vendor)).join('')}
              </div>
            </div>
          `;
        }
      });

      container.innerHTML = html;
    }

    renderBrandLink(vendor) {
      return `
        <a href="${this.escapeHtml(vendor.url)}" class="brands-list__brand-link">
          ${this.escapeHtml(vendor.name)}
        </a>
      `;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define('brands-list', BrandsList);
}
